import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Box,
    Flex,
    Text,
    Button,
    Divider,
    Badge,
    Spinner,
    VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from 'hooks/useCurrency';
import { useCheckout } from 'hooks/useCheckout';

const PLATFORM_MOQ = 500;

function MoqRow({ label, subtitle, total, moq, met, remaining, sym }) {
    const pct = Math.min(100, (total / moq) * 100);
    const fmt = (v) =>
        Number(v ?? 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    const convertedTotal = useCurrency(total);
    const convertedMoq = useCurrency(moq);
    const convertedRemaining = useCurrency(remaining);
    const s = convertedTotal?.symbol ?? sym ?? '$';
    return (
        <Box>
            <Flex justify="space-between" align="flex-start" mb={2} gap={3}>
                <Box>
                    <Text
                        fontSize="13px"
                        fontWeight="500"
                        color="gray.800"
                        _dark={{ color: 'gray.100' }}>
                        {label}
                    </Text>
                    {subtitle && (
                        <Text fontSize="12px" color="gray.500" mt="1px">
                            {subtitle}
                        </Text>
                    )}
                </Box>
                <Box textAlign="right" flexShrink={0}>
                    <Text
                        fontSize="13px"
                        fontWeight="500"
                        color="gray.800"
                        _dark={{ color: 'gray.100' }}>
                        {s} {fmt(convertedTotal?.price)} / {s} {fmt(convertedMoq?.price)}
                    </Text>
                    <Badge
                        mt={1}
                        fontSize="11px"
                        borderRadius="full"
                        px={2}
                        colorScheme={met ? 'green' : 'orange'}>
                        {met || fmt(convertedRemaining?.price) === '0.00'
                            ? 'Met'
                            : `${s} ${fmt(convertedRemaining?.price)} remaining`}
                    </Badge>
                </Box>
            </Flex>
            <Box
                h="8px"
                borderRadius="full"
                bg="gray.100"
                _dark={{ bg: 'gray.700' }}
                overflow="hidden">
                <Box
                    h="100%"
                    borderRadius="full"
                    bg={met ? '#1D9E75' : pct >= 60 ? '#EF9F27' : '#E24B4A'}
                    w={`${pct}%`}
                    transition="width 0.4s ease"
                />
            </Box>
        </Box>
    );
}

export default function CheckoutModal({ isOpen, onClose }) {
    const navigate = useNavigate();
    const { data: checkoutResult, loading: checkoutLoading } = useCheckout({ enabled: isOpen });

    const platformTotal = checkoutResult?.cart_total_price ?? 0;
    const platformMoq = checkoutResult?.platform_moq ?? PLATFORM_MOQ;
    const platformMet = checkoutResult?.platform_moq_met ?? false;
    const platformRemaining = checkoutResult?.remaining_to_platform_moq ?? 0;
    const vendorSummaries = checkoutResult?.vendor_summaries ?? [];
    const canCheckout = checkoutResult?.can_checkout === true;

    const convertedPlatformTotal = useCurrency(platformTotal);
    const sym = convertedPlatformTotal?.symbol ?? '$';
    const fmt = (v) =>
        Number(v ?? 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const handleProceed = () => {
        onClose();
        navigate('/dashboard/checkout');
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" motionPreset="slideInBottom">
            <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
            <ModalContent borderRadius="xl" overflow="hidden" mx={4}>
                <ModalHeader
                    fontSize="15px"
                    fontWeight="500"
                    borderBottom="0.5px solid"
                    borderColor="gray.200"
                    _dark={{ borderColor: 'gray.700' }}
                    pb={3}>
                    Before you checkout
                </ModalHeader>
                <ModalCloseButton size="sm" top={3} right={3} />
                <ModalBody py={5} px={5}>
                    {checkoutLoading ? (
                        <Flex justify="center" align="center" h="200px">
                            <Spinner color="blue.500" size="lg" />
                        </Flex>
                    ) : (
                        <VStack spacing={5} align="stretch">
                            <MoqRow
                                label="Platform minimum"
                                subtitle="Applies to your whole cart"
                                total={platformTotal}
                                moq={platformMoq}
                                met={platformMet}
                                remaining={platformRemaining}
                                sym={sym}
                            />
                            {vendorSummaries.length > 0 && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Text
                                            fontSize="11px"
                                            fontWeight="500"
                                            textTransform="uppercase"
                                            letterSpacing="0.05em"
                                            color="gray.400"
                                            mb={3}>
                                            Vendor minimums
                                        </Text>
                                        <VStack spacing={4} align="stretch">
                                            {vendorSummaries.map((vs) => (
                                                <MoqRow
                                                    key={vs.vendor}
                                                    label={vs.vendor}
                                                    total={vs.vendor_total}
                                                    moq={vs.vendor_moq}
                                                    met={vs.vendor_moq_met}
                                                    remaining={vs.remaining_to_vendor_moq}
                                                    sym={sym}
                                                />
                                            ))}
                                        </VStack>
                                    </Box>
                                </>
                            )}
                            <Divider />
                            <Flex justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="12px" color="gray.500">
                                        Cart total
                                    </Text>
                                    <Text
                                        fontSize="18px"
                                        fontWeight="500"
                                        color="gray.800"
                                        _dark={{ color: 'gray.100' }}
                                        mt="2px">
                                        {sym} {fmt(convertedPlatformTotal?.price)}
                                    </Text>
                                </Box>
                                <Button
                                    bg="#1b3b5f"
                                    color="white"
                                    _hover={{ opacity: 0.85 }}
                                    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                                    isDisabled={!canCheckout}
                                    onClick={handleProceed}
                                    size="md"
                                    borderRadius="lg"
                                    px={6}>
                                    Proceed to checkout
                                </Button>
                            </Flex>
                            {!canCheckout && !checkoutLoading && (
                                <Text fontSize="12px" color="orange.500" textAlign="center" mt={-2}>
                                    Meet all minimum order requirements above to continue.
                                </Text>
                            )}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
